import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const plans = [
  {
    name: "Free",
    price: "$0",
    tagline: "For teams exploring DMARC.",
    features: ["1 monitored domain", "30-day report retention", "Email alerts", "Community support"],
    cta: "Start free",
  },
  {
    name: "Pro",
    price: "$49",
    tagline: "For operational security & deliverability teams.",
    features: [
      "10 monitored domains",
      "12-month retention",
      "Slack + Teams + Webhooks",
      "Sender discovery + classification",
      "SSL / TLS posture + reputation",
      "API access (10k req/day)",
    ],
    cta: "Start Pro trial",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    tagline: "For MSPs, MSSPs, and regulated orgs.",
    features: [
      "Unlimited domains",
      "Multi-tenant MSP console",
      "SSO + RBAC + audit log export",
      "Custom retention",
      "Dedicated edge regions",
      "SLA + named contacts",
    ],
    cta: "Get a quote",
  },
];

export default function Pricing() {
  return (
    <div>
      <section className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">Pricing</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] ink">
            Simple, <span className="font-serif italic font-normal">honest</span> pricing.
          </h1>
          <p className="mt-7 text-lg ink-soft leading-relaxed">
            No deliverability surprise. Scale only when your sender estate grows.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-5">
          {plans.map((p) => (
            <div key={p.name} className={`panel p-8 flex flex-col ${p.featured ? "ring-2 ring-orange-500 ring-offset-2 ring-offset-[#FBF9F4]" : ""}`} data-testid={`plan-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl font-bold ink">{p.name}</div>
                {p.featured && <span className="chip chip-accent">Most adopted</span>}
              </div>
              <p className="mt-2 text-sm ink-soft">{p.tagline}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold ink">{p.price}</span>
                {p.price !== "Custom" && <span className="ink-soft text-sm">/ month</span>}
              </div>
              <ul className="mt-7 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm ink">
                    <CheckCircle2 className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                className={`mt-8 text-center ${p.featured ? "btn-accent justify-center" : "btn-secondary justify-center"}`}
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="panel p-8">
            <h2 className="font-display text-2xl font-bold ink text-center mb-8">Every plan includes</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                "Mandatory DNS ownership verification",
                "Tenant-isolated ingestion",
                "Encrypted-at-rest storage",
                "Audit log access",
                "DMARC / SPF / DKIM / BIMI monitoring",
                "SSL / TLS posture & MTA-STS",
                "Domain reputation tracking",
                "Real-time sender discovery",
              ].map((x) => (
                <div key={x} className="flex items-start gap-2 text-sm ink-soft">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  {x}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
